import PropTypes from 'prop-types';
import { useEffect, useState } from 'react';
import { Document, Page, pdfjs } from 'react-pdf';

// ✅ Required worker setup - served locally to avoid CDN/network failures
pdfjs.GlobalWorkerOptions.workerSrc = `/pdf.worker.min.mjs`;

function PDFViewer({ url, isFullScreen = false, onError }) {
  const [numPages, setNumPages] = useState(null);
  const [pageNumber, setPageNumber] = useState(1);
  const [scale, setScale] = useState(1);
  const [internalError, setInternalError] = useState(null);
  const [isRendered, setIsRendered] = useState(false);

  // ✅ Reset when URL changes
  useEffect(() => {
    console.log('PDFViewer: Loading URL:', url);
    setNumPages(null);
    setPageNumber(1);
    setScale(1);
    setInternalError(null);
    setIsRendered(false);
  }, [url]);

  function onDocumentLoadSuccess({ numPages }) {
    console.log('PDFViewer: Document loaded successfully. Total pages:', numPages);
    setNumPages(numPages);
    setInternalError(null);
    onError?.(null);
  }

  function onDocumentLoadError(error) {
    console.error('Error loading PDF:', error);
    setInternalError(`Failed to load PDF: ${error.message || 'Unknown error'}`);
    onError?.(`Failed to load PDF: ${error.message || 'Unknown error'}. Please try downloading the file instead.`);
  }

  const goToPrevPage = (e) => {
    e.stopPropagation();
    setPageNumber((prev) => Math.max(prev - 1, 1));
  };

  const goToNextPage = (e) => {
    e.stopPropagation();
    setPageNumber((prev) =>
      numPages ? Math.min(prev + 1, numPages) : prev
    );
  };

  const zoomIn = (e) => {
    e.stopPropagation();
    setScale((prev) => Math.min(prev + 0.1, 2));
  };

  const zoomOut = (e) => {
    e.stopPropagation();
    setScale((prev) => Math.max(prev - 0.1, 0.5));
  };

  const resetZoom = (e) => {
    e.stopPropagation();
    setScale(1);
  };

  const renderToolbarButton = (onClick, disabled, icon, label) => (
    <button
      onClick={onClick}
      disabled={disabled}
      className="flex items-center gap-2 px-3 py-2 text-sm text-gray-700 hover:bg-gray-100 rounded-md disabled:opacity-50 disabled:cursor-not-allowed"
      title={label}
      type="button"
    >
      {icon}
      <span className="hidden sm:inline">{label}</span>
    </button>
  );

  // ✅ Configuration for cmaps (needed for some PDFs)
  const options = {
    cMapUrl: `https://unpkg.com/pdfjs-dist@${pdfjs.version}/cmaps/`,
    cMapPacked: true,
    standardFontDataUrl: `https://unpkg.com/pdfjs-dist@${pdfjs.version}/standard_fonts/`,
  };

  if (!url) {
    return (
      <div className="text-center text-gray-400 py-10">
        No PDF available
      </div>
    );
  }

  if (internalError) {
    return (
      <div className="text-center text-red-500 py-10">
        {internalError}
      </div>
    );
  }

  if (isFullScreen) {
    return (
      <div className="flex flex-col h-full bg-white">
        <div className="flex-1 overflow-auto flex justify-center items-start p-4 bg-gray-100">
          <Document
            file={url} // Directly pass the blob URL string
            options={options}
            onLoadSuccess={onDocumentLoadSuccess}
            onLoadError={onDocumentLoadError}
            loading={
              <div className="text-gray-600 text-center py-4">
                Loading PDF...
              </div>
            }
          >
            <Page
              pageNumber={pageNumber}
              scale={scale}
              renderTextLayer={false}
              renderAnnotationLayer={false}
              className="shadow-lg"
              onRenderSuccess={() => console.log(`PDFViewer: Page ${pageNumber} rendered successfully.`)}
            />
          </Document>
        </div>

        {/* Toolbar */}
        <div className="flex items-center justify-between gap-4 px-4 py-2 bg-white border-t">
          <div className="flex items-center gap-2">
            {renderToolbarButton(
              goToPrevPage,
              pageNumber <= 1,
              <span>◀</span>,
              'Previous'
            )}

            <span className="text-sm text-gray-600">
              Page {pageNumber} of {numPages || '--'}
            </span>

            {renderToolbarButton(
              goToNextPage,
              !numPages || pageNumber >= numPages,
              <span>▶</span>,
              'Next'
            )}
          </div>

          <div className="flex items-center gap-2">
            {renderToolbarButton(zoomOut, scale <= 0.5, <span>-</span>, 'Zoom Out')}
            {renderToolbarButton(resetZoom, scale === 1, <span>⟳</span>, 'Reset')}
            {renderToolbarButton(zoomIn, scale >= 2, <span>+</span>, 'Zoom In')}
          </div>
        </div>
      </div>
    );
  }

  // ✅ Preview Mode
  return (
    <div className="relative bg-white max-h-[60vh] overflow-hidden border rounded-lg shadow-sm">
      <Document
        file={url}
        options={options}
        onLoadSuccess={onDocumentLoadSuccess}
        onLoadError={onDocumentLoadError}
        loading={
          <div className="flex items-center justify-center h-[200px] text-gray-400 text-sm italic">
            Preparing Preview...
          </div>
        }
      >
        <Page
          pageNumber={1}
          width={330}
          renderTextLayer={false}
          renderAnnotationLayer={false}
          onRenderSuccess={() => console.log('PDFViewer: Preview page 1 rendered successfully.')}
        />
      </Document>

      <div className="absolute inset-0 bg-gradient-to-b from-transparent via-transparent to-gray-100/90 flex items-end justify-center p-3 pointer-events-none">
        <div className="text-sm text-gray-600">
          Click to view PDF
        </div>
      </div>
    </div>
  );
}

PDFViewer.propTypes = {
  url: PropTypes.string.isRequired,
  isFullScreen: PropTypes.bool,
  onError: PropTypes.func,
};

export default PDFViewer;